import { useCallback, useEffect } from 'react';

import { canOpen } from '../lib/access/screenAccess';
import { fetchMyAccess } from '../lib/access/myAccess';
import { logError } from '../lib/logger';
import { useAccessStore, type AccessState } from '../store/accessStore';
import { useAuth } from './useAuth';

/** One read at a time per account, whatever the number of screens that ask. */
const inflight = new Map<string, Promise<void>>();

/** Reads `my_access()` for the account into the store. Exported for the tests and for a retry. */
export function loadAccess(userId: string): Promise<void> {
  const running = inflight.get(userId);
  if (running) return running;
  const { startLoading, setAccess, setError } = useAccessStore.getState();
  startLoading(userId);
  const read = fetchMyAccess()
    .then((access) => setAccess(userId, access))
    .catch((err: unknown) => {
      logError('access', 'my_access unreadable', err, { userId });
      setError(userId, 'Vos droits d’accès n’ont pas pu être lus. Vérifiez la connexion puis réessayez.');
    })
    .finally(() => inflight.delete(userId));
  inflight.set(userId, read);
  return read;
}

const NOT_READ: AccessState = { status: 'idle' };

/**
 * What the signed-in person can, read once per account and shared by every screen (lot L6g). While nothing is read for
 * the current account, `idle` or `loading`; `retry` reads it again after an error.
 */
export function useMyAccess(): { readonly state: AccessState; readonly retry: () => void } {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const state = useAccessStore((s) => s.state);

  useEffect(() => {
    if (userId === null) {
      useAccessStore.getState().reset();
      return;
    }
    const current = useAccessStore.getState().state;
    if (current.status === 'idle' || current.userId !== userId) void loadAccess(userId);
  }, [userId]);

  const retry = useCallback(() => {
    if (userId !== null) void loadAccess(userId);
  }, [userId]);

  // A state read for another account (the one before a sign-in) is not this one's.
  const own = state.status === 'idle' || state.userId === userId ? state : NOT_READ;
  return { state: own, retry };
}

/** Whether a path may be opened by the signed-in person; false until their access is read (the menus wait for it). */
export function useCanOpen(): (pathname: string) => boolean {
  const { state } = useMyAccess();
  const access = state.status === 'ready' ? state.access : null;
  return useCallback((pathname: string) => access !== null && canOpen(pathname, access), [access]);
}
